import { faBedPulse, faChevronDown, faChevronUp, faCircle, faRefresh } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { Badge, Button, Card, Col, OverlayTrigger, Placeholder, Row, Tab, Tabs, Tooltip } from "react-bootstrap";
import { EXPLAIN_MONITOR, SCAN_MULTI } from "../../../../amplify/api-gateway/write";
import { LLMDashboardFeedbackBar } from "../common/LLMDashboardFeedbackBar";
import { LLMDashboardUpdated } from "../common/LLMDashboardUpdated";
import { SectionMonitoringSummaryDetails } from "./SectionMonitoringSummaryDetails";
import { GenerateModuleHeroV2 } from "../../../common/GenerateModuleHeroV2";
import { FakeLoadingBar } from "../common/FakeLoadingBar";
import { isSectionUnread } from "../utils";
import { dashboardLLMCache } from "../cache";
import { LLMDashboardMarkdownRenderer } from "../common/LLMDashboardMarkdownRenderer";
import { ModuleErrorDisplay } from "../common/ModuleErrorDisplay";

export const SectionMonitoringSummary = ({
    className='',
    isLoadingMonitoringSummary,
    monitoringSummary,
    patientVisit,
    feedbacks,
    setFeedbacks,
    statuses,
    setRegenerateTarget,
    setShowContextMenu,
    handleShowScanMultiModal,
    tenant,
    unreadFileVersionMap = undefined,
    latestFileVersions = undefined,
    sectionToSetter = undefined,
    handleFileVersionChange = undefined,
}: any) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const [activeSection, setActiveSection] = useState("monitoring_description");

    const [scanMulti, setScanMulti] = useState();

    useEffect(() => {
        // It's important to load the version of the file that the explain data
        // was generated from to ensure the citations line up
        if (patientVisit && monitoringSummary && latestFileVersions && latestFileVersions[EXPLAIN_MONITOR]) {
            const version = latestFileVersions[EXPLAIN_MONITOR];

            dashboardLLMCache.get(SCAN_MULTI, patientVisit, tenant, version.source_version_id)
            .then((data: any) => {
                setScanMulti(data);
            })
        }
    }, [latestFileVersions, monitoringSummary]);

    return (
        <Card className={`fade-in ${className}`}>
            <FakeLoadingBar isActive={isLoadingMonitoringSummary} maxDuration={30000} />
            <Card.Body>
                <Row>
                    <Col md={6}>
                        <div className="d-flex align-items-center">
                            <span className={`dashboard-actions-circle-small dashboard-actions-circle-blue me-2`}><FontAwesomeIcon icon={faBedPulse} fixedWidth /></span><strong>Monitor Summary</strong> {isSectionUnread(EXPLAIN_MONITOR, unreadFileVersionMap) && <Badge bg="info" className="ms-2">NEW</Badge>}
                        </div>
                    </Col>
                    <Col md={6} className="text-right">
                        <LLMDashboardUpdated patient={patientVisit} statuses={statuses} handleShowScanMultiModal={handleShowScanMultiModal} latestFileVersions={latestFileVersions} explainKey={EXPLAIN_MONITOR} sectionToSetter={sectionToSetter} handleFileVersionChange={handleFileVersionChange} />
                        <OverlayTrigger
                            placement="bottom"
                            overlay={
                                <Tooltip>
                                    Refresh Section
                                </Tooltip>
                            }
                        >
                            <Button
                                variant="primary"
                                className="dashboard-round-btn"
                                onClick={(e: any) => {e.stopPropagation(); setRegenerateTarget(EXPLAIN_MONITOR); setShowContextMenu(true)}}
                            >
                                <FontAwesomeIcon icon={faRefresh} />
                            </Button>
                        </OverlayTrigger>
                        <LLMDashboardFeedbackBar patientVisit={patientVisit} feedbackTarget={"monitoring"} feedbacks={feedbacks} setFeedbacks={setFeedbacks} />
                        <Button
                            variant="primary"
                            className="dashboard-round-btn"
                            onClick={(e: any) => {e.stopPropagation(); setIsExpanded(!isExpanded)}}
                        >
                            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                        </Button>
                    </Col>
                </Row>
                {isExpanded && (
                    <Row className="mt-2">
                        <Col md={12} className="description-text">
                            {monitoringSummary && monitoringSummary["error"] ? (
                                <ModuleErrorDisplay
                                    errorMessage={monitoringSummary["error"]}
                                    moduleTitle="Monitor Summary"
                                    hasPreviousVersions={!!(latestFileVersions && latestFileVersions[EXPLAIN_MONITOR])}
                                />
                            ) : monitoringSummary && monitoringSummary[activeSection] ? (
                                <Row className="fade-in">
                                    <Col md={6}>
                                        <Tabs
                                            activeKey={activeSection}
                                            onSelect={(k: any) => setActiveSection(k)}
                                            className="mb-3"
                                            variant="pills"
                                        >
                                            <Tab eventKey="monitoring_description" title="Monitoring Description">
                                                <p>
                                                    {monitoringSummary && monitoringSummary[activeSection] && (
                                                        <LLMDashboardMarkdownRenderer content={monitoringSummary[activeSection].summary} scanMulti={scanMulti} />
                                                    )}
                                                </p>
                                            </Tab>
                                            <Tab eventKey="clinical_interpretation" title="Clinical Interpretation">
                                                <p>
                                                    {monitoringSummary && monitoringSummary[activeSection] && (
                                                        <LLMDashboardMarkdownRenderer content={monitoringSummary[activeSection].summary} scanMulti={scanMulti} />
                                                    )}
                                                </p>
                                            </Tab>
                                            <Tab eventKey="recommendations" title="Recommendations">
                                                <p>
                                                    {monitoringSummary && monitoringSummary[activeSection] && (
                                                        <LLMDashboardMarkdownRenderer content={monitoringSummary[activeSection].summary} scanMulti={scanMulti} />
                                                    )}
                                                </p>
                                            </Tab>
                                        </Tabs>
                                    </Col>
                                    <Col md={6}>
                                        {
                                            monitoringSummary && monitoringSummary[activeSection] && monitoringSummary[activeSection].evidence && (
                                                <SectionMonitoringSummaryDetails data={monitoringSummary[activeSection]} scanMulti={scanMulti} />
                                            )
                                        }
                                    </Col>
                                </Row>
                            ) : (
                                <>No Data</>
                            )}
                        </Col>
                    </Row>
                )}
            </Card.Body>
        </Card>
    );
};
